import React, { useState, useEffect, useContext } from "react";
import { notify } from "react-notify-toast";
import { Box, Center, Text, Stack, Button, FormControl, FormLabel, Input, HStack, useToast, VStack } from "@chakra-ui/react";
import {  Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from "@chakra-ui/react";
import { Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow } from "@chakra-ui/react";
import { ModalCloseButton, Container, Divider, useDisclosure, useClipboard, Flex, Checkbox, Card, Heading } from "@chakra-ui/react";
import { SketchPicker } from "react-color";
import { BiCopy } from "react-icons/bi";
import { MdOutlineRefresh } from "react-icons/md";

import { InfoOutlineIcon, CopyIcon } from "@chakra-ui/icons";

import { getUserName } from "../App/useToken";
import { callApiWithToken } from "../../utils/utils";
import { NavBarContext } from "../../context/NavBarContext";
import { EnvTypeContext } from "../../context/EnvTypeContext";
import { TimezoneContext } from "../../context/TimezoneContext";
import Loader from "../../utils/Loader";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import { useTranslation } from "react-i18next";

import "../../Styles.css";
import { getDataFromSession } from "../../utils/session-storing-utils";
import MerchantKeyUpdateModal from "./modals/MerchantKeyUpdateModal";
import WebhookSecretUpdateModal from "./modals/WebhookSecretUpdateModal";
import { getKybStatus } from "../../apiService/kybForm-service";
import { useNavigate } from "react-router-dom";

export default function Settings() {
    const { t } = useTranslation();
    const permissions = getDataFromSession("userData", "permissions") || [];
    const canUpdateSettings = permissions.includes("settings.update");
    const [language, setLanguage] = useState("");
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [secondaryColorPicker, setSecondaryColorPicker] = useState(false);
    const [webhookData, setWebhookData] = useState({});
    const [orgData, setOrgData] = useState({});
    /* TransFi logo color default */
    const [colorCode, setColorCode] = useState("");
    const [secondaryColor, setSecondaryColor] = useState("");
    // const [walletAddress, setWalletAddress] = useState("");
    const [loading, setLoading] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState("");
    const [redirectUrl, setRedirectUrl] = useState("");
    const [orgName, setOrgName] = useState("");


    const toast = useToast();
    const { selectedOrg } = useContext(NavBarContext);
    const { env } = useContext(EnvTypeContext);
    
    useEffect(() => {
        getCustomerData();
        getDefaultBrowserLanguage();
    }, [env]);

    const updateSettings = async () => {
        const payload = {
            impersonatedOrg: selectedOrg,
            language,
            // walletAddress,
            primaryColor: colorCode,
            secondaryColor,
            webhookUrl,
            redirectUrl,
            key: env === "Live" ? "webhook.prdUrl" : "webhook.url",
        };

        if (!validateForm()) {
            try{
                setLoading(true);
                const response = (await callApiWithToken("POST", "/api/organization/update", payload)) || {};
                if(response.status !== true) {
                    throw new Error("Failed to update settings");
                }
                notify.show("Updated Successfully..", "custom", 5000, { background: "#4BB543", text: "white" });
            }catch(error){
                notify.show("Failed to update settings", "custom", 5000, { background: "#FF0000", text: "white" });
            } finally{
                setLoading(false);
            }
        }
    };

    const getCustomerData = async () => {
        try {
            setLoading(true);
            const response =
                (await callApiWithToken("POST", "/api/organization/details", { impersonatedOrg: selectedOrg })) || {};
            const { orgResponse = {}, webhookResponse = {} } = response;
            const { primaryColor = "#1A4FD6", language, secondaryColor = "#232D95", formattedName } = orgResponse;
            const { webhook = {}, redirectUrl = "" } = webhookResponse || {};
            const newWebhookUrl = env === "Sandbox" ? webhook.url || "" : webhook.prdUrl || "";
            if(response.status !== true) {
                console.error("Error fetching data", response);
                throw new Error("Failed to fetch data");
            }
            setWebhookData(webhook);
            setOrgData(orgResponse);
            setColorCode(primaryColor || "#1A4FD6");
            setSecondaryColor(secondaryColor || "#232D95");
            setLanguage(language);
            setLoading(false);
            setOrgName(formattedName);
            setRedirectUrl(redirectUrl);
            setWebhookUrl(newWebhookUrl);
        } catch(err) {
            console.error("Error fetching data", err);
            notify.show("Failed to fetch data", "custom", 5000, { background: "#FF0000", text: "white" });
        } finally {
            setLoading(false);
        }
       
    };

    const getDefaultBrowserLanguage = () => {
        /* Call in case default language is not set. This picks up browser language and uses it as default */
        const browserLangComplete = navigator.language || navigator.userLanguage;
        const browserLang = browserLangComplete.split("-")[0];

        if (browserLang && typeof browserLang === "string" && browserLang.toLowerCase() === "en") {
            setLanguage("english");
        }
    };

    const validateForm = () => {
        let isError = false;
        return isError;
    };

    const toggleColorPicker = () => setShowColorPicker(!showColorPicker);
    const toggleSecondaryColor = () => setSecondaryColorPicker(!secondaryColorPicker);

    const handleColorChange = ({ hex }) => {
        setColorCode(hex);
        setShowColorPicker(!showColorPicker);
    };
    const handleSecondaryColor = ({ hex }) => {
        setSecondaryColor(hex);
        setSecondaryColorPicker(!secondaryColorPicker);
    };

    const handleUrlChange = (event) => {
        setWebhookUrl(event.target.value);
    };

    const handleRedirectUrlChange = (event) => {
        setRedirectUrl(event.target.value);
    };

    return (
        loading ?
            <Loader /> :
            <Center py={5} bg={"#F2F2F2"}>
                <Stack spacing={2} w="90%">
                    <Box bg={"#F2F2F2"} p={4}>
                        <Stack spacing={5}>
                            <Text fontSize="1xl" p={5} color="black" fontWeight={"bold"}>
                                {t("settings:welcome")}, {getUserName()}
                            </Text>
                            {/* <Divider /> */}
                            <TimeZoneSection 
                                loading={loading}
                                setLoading={setLoading}
                                canUpdateSettings={canUpdateSettings}
                            />

                            <Card p={12} bg={"white"} borderRadius={10} boxShadow="sm">
                                <Stack w="100%">
                                    <HStack alignItems={"center"}>
                                        <FormControl size="sm">
                                            <FormLabel htmlFor="text" pt={2}>
                                                {t("settings:primary_color")}
                                            </FormLabel>
                                            <HStack>
                                                <Button
                                                    variant={"ghost"}
                                                    iconSpacing={0}
                                                    size="sm"
                                                    onClick={toggleColorPicker}
                                                    bgColor={colorCode}
                                                    isDisabled={!canUpdateSettings}
                                                    onClose={handleColorChange}
                                                ></Button>
                                                <Box className="colorPicker">
                                                    {showColorPicker ? (
                                                        <SketchPicker
                                                            disableAlpha={true}
                                                            color={colorCode}
                                                            onChangeComplete={handleColorChange}
                                                        />
                                                    ) : null}
                                                </Box>
                                                <Input
                                                    bg="gray.100"
                                                    value={colorCode}
                                                    size="sm"
                                                    isReadOnly
                                                    onFocus={toggleColorPicker}
                                                    disabled={!canUpdateSettings}
                                                />
                                            </HStack>
                                        </FormControl>

                                        <FormControl size={"sm"}>
                                            <FormLabel htmlFor="text" pt={2}>
                                                {t("settings:secondary_color")}
                                            </FormLabel>
                                            <HStack>
                                                <Button
                                                    variant={"ghost"}
                                                    iconSpacing={0}
                                                    size="sm"
                                                    onClick={toggleSecondaryColor}
                                                    bg={secondaryColor}
                                                    isDisabled={!canUpdateSettings}
                                                ></Button>
                                                <Box className="colorPicker">
                                                    {secondaryColorPicker ? (
                                                        <SketchPicker
                                                            disableAlpha={true}
                                                            color={secondaryColor}
                                                            onChangeComplete={handleSecondaryColor}
                                                        />
                                                    ) : null}
                                                </Box>
                                                <Input
                                                    bg="gray.100"
                                                    value={secondaryColor}
                                                    size="sm"
                                                    isReadOnly
                                                    disabled={!canUpdateSettings}
                                                    onFocus={toggleSecondaryColor}
                                                />
                                            </HStack>
                                        </FormControl>
                                    </HStack>


                                    {/* {
                                        orgName === "binance" &&
                                        <HStack alignItems={"center"}>

                                            <FormControl size="sm">
                                                <FormLabel htmlFor="text" pt={2}>
                                                    Buy Fees(%)
                                                </FormLabel>

                                                <Input
                                                    disabled
                                                    value={1.8}
                                                    width={"80%"}
                                                    variant="outline"
                                                />

                                            </FormControl>

                                            <FormControl size="sm">
                                                <FormLabel htmlFor="text" pt={2}>
                                                    Sell Fees (%)
                                                </FormLabel>
                                                <Input
                                                    disabled
                                                    value={1.5}
                                                    width={"80%"}
                                                    variant="outline"
                                                />
                                            </FormControl>
                                        </HStack>
                                    } */}
                                    <FormControl size="sm">
                                        <FormLabel htmlFor="text" pt={2}>
                                            {t("settings:webhook_URL")}
                                        </FormLabel>
                                        <HStack>
                                            <Input
                                                value={webhookUrl}
                                                variant="outline"
                                                placeholder="Enter webhook url"
                                                onChange={handleUrlChange}
                                                disabled={!canUpdateSettings}
                                            />
                                        </HStack>
                                    </FormControl>
                                    <FormControl size="sm">
                                        <FormLabel htmlFor="text" pt={2}>
                                            {t("settings:redirect_URL")}
                                        </FormLabel>
                                        <HStack>
                                            <Input
                                                value={redirectUrl}
                                                variant="outline"
                                                placeholder="Enter Redirect url"
                                                onChange={handleRedirectUrlChange}
                                                disabled={!canUpdateSettings}
                                            />
                                        </HStack>
                                    </FormControl>


                                    <HStack justifyContent={"end"} style={{ marginTop: "5%" }}>
                                       {canUpdateSettings && <Button
                                            isLoading={loading}
                                            variant={"primary"}
                                            bg={"#1A4FD6"}
                                            _hover={{ bg: "#366cf5" }}
                                            color="white"
                                            onClick={updateSettings}
                                            size="md"
                                            w={"200px"}
                                            rounded={"10px"}
                                        >
                                            {t("users:update")}
                                        </Button>}
                                    </HStack>
                                </Stack>
                            </Card>
                            {/*
                                <KYBSection 
                                orgData={orgData}
                                permissions={permissions}
                                t={t}
                                />
                            */}
                            <SecretKeysSection 
                                permissions ={permissions}
                                orgData={orgData}
                                webhookData={webhookData}
                            />
                        </Stack>
                    </Box>
                </Stack >
            </Center >
    );
}

function TimeZoneSection({canUpdateSettings, loading, setLoading}) {
    const { defaultTimezone, setDefaultTimezone } = useContext(TimezoneContext);
    const [timezone, setTimezone] = useState(defaultTimezone);
    const { t } = useTranslation();
    const [isChecked, setIsChecked] = useState(true);

    const handleCheckboxChange = () => {
        setIsChecked(!isChecked);
    };

    const { isOpen: isTimezoneOpen, onOpen: onTimezoneOpen, onClose: onTimezoneClose } = useDisclosure();

    const timezoneStyles = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            width: '450px',
            height: "20px",
            fontSize: "14px",
            borderColor: "grey"
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: '14px',
        }),
    };


    const onClickTimezoneUpdate = async () => {
        setLoading(true);
        try {
            const payload = { timezone };
            const response = await callApiWithToken("POST", "/api/organization/updateTimezone", payload) || {};
            if(response.status !== true) {
               throw new Error("Failed to update settings");
            }
            
            notify.show("Updated Successfully..", "custom", 5000, { background: "#4BB543", text: "white" });
            setDefaultTimezone(timezone);
            sessionStorage.setItem('defaultTimezone', timezone);
            if (isChecked === true) window.location.reload();
        } catch (error) {
            console.log(error);
            notify.show("Failed to update settings", "custom", 5000, { background: "#FF0000", text: "white" });
        } finally {
            setLoading(false);
            onTimezoneClose();
        }
    };

    return (
        <Card p={12} bg={"white"} borderRadius={10} boxShadow="sm">
            <Box >
                <Flex display={"column"}>
                    <FormLabel htmlFor="text" pt={2}>
                        {t("settings:timezone")}
                    </FormLabel>
                    <HStack justifyContent={"space-between"} alignItems={"center"}>
                        <Flex direction={"column"} gap={4}>
                            <TimezoneSelect
                                styles={{ ...timezoneStyles }}
                                isDisabled={!canUpdateSettings || loading}
                                value={timezone}
                                onChange={(value) => setTimezone(value?.value)}
                                timezones={{ ...allTimezones }}
                            />
                            <Checkbox isChecked={isChecked} onChange={handleCheckboxChange} disabled={loading || !canUpdateSettings}>{t("settings:refresh_the_dashboard_with_new_timezone")}</Checkbox>
                        </Flex>
                        {canUpdateSettings && (
                            <Button
                                onClick={onClickTimezoneUpdate}
                                variant={"primary"}
                                bg={"#1A4FD6"}
                                _hover={{ bg: "#366cf5" }}
                                color="white"
                                size="md"
                                rounded={"10px"}
                                w={"200px"}
                                loading={loading}
                                alignSelf={"start"}
                            >
                                {t("settings:change_timezone")}
                            </Button>
                        )}
                    </HStack>
                </Flex>
            </Box>
        </Card>
    )
}

function SecretKeysSection ({orgData ={}, webhookData={}, permissions}) {
    const username = orgData?.formattedName || "";
    const password = orgData?.merchantKey || "";
    // const webhookSecret = webhookData?.secret || "";
    const { onCopy: onUsernameCopy } = useClipboard(username);
    const { onCopy: onPasswordCopy } = useClipboard(password);
    const apiKey = orgData?.apiKey || (orgData?.apiKeys || [])[0] || ''; 
    

    const { isOpen: isPasswordModalOpen, onOpen: onPasswordModalOpenClick, onClose: onPasswordModalClose } = useDisclosure();
    const { isOpen: isSecretModalOpen, onOpen: onSecretModalOpenClick, onClose: onSecretModalClose } = useDisclosure();

    const canRegenerateMerchantKey = permissions?.includes("merchant_key.update");
    const canRenerateWebhookSecretKey = permissions?.includes("webhook_secretkey.update");

    return (
        <Card p={12} bg={"white"} borderRadius={10} boxShadow="sm">
            <Box bg="white" >
                <Flex justifyContent={"space-between"} alignItems={"center"}>
                    <Text fontWeight={"semibold"} mb={4}>Webhooks</Text>
                </Flex>
                <Flex justifyContent={"flex-start"} alignItems={"center"} gap={4}>
                    <Text fontWeight={"normal"} w={"80px"}>
                    Webhook Secret Key
                    </Text>

                    <Button display={"flex"} justifyContent={"space-between"} width={"350px"} variant="outline"  onClick={onPasswordCopy}>
                        <Text className="truncate" fontWeight={"normal"}>
                            ****************************
                        </Text>
                    </Button>
                    <Flex>
                    {canRenerateWebhookSecretKey && (
                        <MdOutlineRefresh
                        style={{ fontSize: "20px" }}
                        className="transition-all hover:rotate-180 cursor-pointer"
                        onClick={onSecretModalOpenClick}
                        />
                    )}
                    </Flex>
                </Flex>
            </Box>

            <Box bg="white" marginTop={12}>
                <Text fontWeight={"semibold"} mb={4}>
                    API Authentication
                </Text>
                <Flex justifyContent={{ md: "flex-start" }} alignItems={{ md: "flex-start" }} direction={{ md: "column" }} gap={4}>
                    <Flex justifyContent={"flex-start"} alignItems={"center"} gap={4}>
                        <Text fontWeight={"normal"} w={"80px"} >
                            Username
                        </Text>
                        <Button
                            display={"flex"}
                            justifyContent={"space-between"}
                            width={"350px"}
                            variant="outline"
                            rightIcon={
                            <Popover>
                                <PopoverTrigger>
                                    <Button m={0} p={0} bg={"transparent"} height={"max-content"} onClick={onUsernameCopy}>
                                        <CopyIcon />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent width={"fit-content"}>
                                    <PopoverArrow />
                                    <PopoverBody>Copied</PopoverBody>
                                </PopoverContent>
                            </Popover>
                            }
                            onClick={onUsernameCopy}
                        >
                            <Text fontWeight={"normal"}>{username}</Text>
                        </Button>
                        <Popover placement="right">
                            <PopoverTrigger>
                            <InfoOutlineIcon style={{ marginBottom: "6px", fontSize: "18px" }} />
                            </PopoverTrigger>
                            <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody>"It's your unique user name to create the authentication token for using TransFi API."</PopoverBody>
                            </PopoverContent>
                        </Popover>
                    </Flex>
                    <Flex justifyContent={"flex-start"} alignItems={"center"} gap={4}>
                        <Text fontWeight={"normal"} w={"80px"} >
                            Password
                        </Text>

                        <Button display={"flex"} justifyContent={"space-between"} width={"350px"} variant="outline" onClick={onPasswordCopy}>
                            <Text className="truncate" fontWeight={"normal"}>
                            ****************************
                            </Text>
                        </Button>
                        <Flex>
                            {canRegenerateMerchantKey && (
                            <MdOutlineRefresh
                                style={{ fontSize: "20px" }}
                                className="transition-all hover:rotate-180 cursor-pointer"
                                onClick={onPasswordModalOpenClick}
                            />
                            )}
                        </Flex>
                    </Flex>
                    <Flex gap={4} justifyContent={"flex-start"} alignItems={"center"}>
                        <Text fontWeight={"normal"} w={"80px"}>API Key</Text>
                        <Input type="text" value={apiKey} readOnly w="350px"/>
                    </Flex>
                </Flex>
            </Box>

            <MerchantKeyUpdateModal isOpen={isPasswordModalOpen} onClose={onPasswordModalClose} />
            <WebhookSecretUpdateModal isOpen={isSecretModalOpen} onClose={onSecretModalClose} type="webhook" />
        </Card>
    )
}

function KYBSection ({orgData = {}, permissions =[], t}) {
    const { kybStatusValue, setKybStatusValue } = useContext(NavBarContext);
    const orgId = orgData?.orgId || "";
    const grpOrgId = sessionStorage.getItem('grpOrgId');
    const isOceanGaming = (orgId === "OG-220923101501950" && grpOrgId === "OG-220923101501950");
    const canUpdateKYB = permissions?.includes("kyb.update");
    const navigate = useNavigate();

    useEffect(() => {
        getKybStatusData();
    }, []);

    const getKybStatusData = async () => {
        const { kybStatus = "" } = await getKybStatus();
        setKybStatusValue(kybStatus);
    };

    return (
        <div>
            {(kybStatusValue == "pending" && !isOceanGaming) &&
                (
                <Box width={"100%"} p="10" bg="#FFF4D7" borderRadius={"16px"}>
                    <VStack gap={5} alignItems={"left"}>
                    <Heading as="h5" size="md">
                        Start your KYB verification{" "}
                    </Heading>
                    <Text fontSize={"18px"}>
                        Before going live in production you must provide your company
                        information and sign the agreement to get KYB process
                        implemented
                    </Text>
                   {canUpdateKYB && <Box textAlign={"center"}>
                        <Button
                        onClick={() => navigate("/developers/kyb-form")}
                        colorScheme="blue"
                        bg={"#1A4FD6"}
                        _hover={{ bg: "#232D95" }}
                        size="lg"
                        pl={10}
                        pr={10}
                        >
                        Proceed to your KYB form
                        </Button>
                    </Box>}
                    </VStack>
                </Box>
                )
            }
            {kybStatusValue == "completed" && (
                <Box width={"100%"} p="5" bg="#DAFFD7" borderRadius={"16px"}>
                <Text>{t("settings:go_live")}</Text>
                </Box>
            )}
        </div>
    )
}